import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Stack,
  Paper,
  Fade,
} from '@mui/material'
import localization from '../utils/localization'
import copy from 'copy-to-clipboard'
import toast from 'react-hot-toast'

const WalletDetailRow = ({ label, value, icon, color, isPassword }) => {
  const [hover, setHover] = useState(false)
  const [show, setShow] = useState(!isPassword)

  if (!value) return null

  const handleCopy = () => {
    copy(value)
    toast.success(localization.strings.copySuccess)
  }

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        p: 2.5,
        borderRadius: '20px',
        border: '1px solid rgba(0,0,0,0.04)',
        backgroundColor: '#fff',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.05)',
          borderColor: color,
        },
      }}
    >
      {/* Top Right Actions */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          opacity: hover ? 1 : 0.4,
          transition: 'opacity 0.2s ease',
        }}
      >
        {isPassword && (
          <Box
            onClick={() => setShow(!show)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#64748B',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: color },
            }}
          >
            <i
              className={`iconfont ${show ? 'icon-eye' : 'icon-eyeslash'}`}
              style={{ fontSize: '1.1rem' }}
            ></i>
          </Box>
        )}
        <Box
          onClick={handleCopy}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#64748B',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: color },
          }}
        >
          <i className="iconfont icon-copy" style={{ fontSize: '1.1rem' }}></i>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            backgroundColor: `${color}10`,
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <i className={`iconfont ${icon}`} style={{ fontSize: '1.3rem' }}></i>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pr: 6 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#94A3B8',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: '#1E293B',
              wordBreak: 'break-all',
              fontSize: '1.05rem',
              fontFamily: isPassword ? "'JetBrains Mono', monospace" : 'inherit',
              letterSpacing: !show ? '0.2em' : 'normal',
              whiteSpace: show ? 'pre-wrap' : 'nowrap'
            }}
          >
            {show ? value : '••••••••'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

function ModalWallet(props) {
  const detail = props.contentInfo?.detail || {}

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          p: 1,
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 4,
          pb: 1,
          color: '#1E293B',
        }}
      >
        {localization.strings.walletDetail}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 4 }}>
        <Fade in={props.open} timeout={500}>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={1.5}>
              <WalletDetailRow
                label={localization.strings.title}
                value={props.contentInfo?.title}
                icon="icon-tag1"
                color="#1E88E5"
              />
              <WalletDetailRow
                label={localization.strings.coinName}
                value={detail.coinName}
                icon="icon-bitcoin"
                color="#F59E0B"
              />
              <WalletDetailRow
                label={localization.strings.address}
                value={detail.address}
                icon="icon-link"
                color="#10B981"
              />

              <Box sx={{ height: 8 }} />
              <Typography
                variant="caption"
                sx={{ color: '#94A3B8', fontWeight: 800, px: 1, mb: 1, display: 'block' }}
              >
                PRIVATE CREDENTIALS
              </Typography>

              <WalletDetailRow
                label={localization.strings.pubkey}
                value={detail.pubKey}
                icon="icon-key1"
                color="#6366F1"
                isPassword={true}
              />
              <WalletDetailRow
                label={localization.strings.privKey}
                value={detail.priKey}
                icon="icon-lock"
                color="#EF4444"
                isPassword={true}
              />
              <WalletDetailRow
                label="KeyStore"
                value={detail.keyStore}
                icon="icon-lock"
                color="#EF4444"
                isPassword={true}
              />
              <WalletDetailRow
                label={localization.strings.mnemonic}
                value={detail.hintWord}
                icon="icon-file-text"
                color="#8B5CF6"
                isPassword={true}
              />
            </Stack>
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  )
}

export default ModalWallet
